import BackToPageCTA from "../reusables/BackToPageCTA/BackToPageCTA";
import CustomNavbar from "../reusables/CustomNavbar/CustomNavbar";
import "./AbTestStyles.css";
import { useEffect, useState, useContext } from "react";
import { SelectedProjectContext } from "../../contexts";
import { useNavigate, useLocation } from "react-router-dom";
import AbTestStep1 from "./AbTestStep1";
import AbTestStep2 from "./AbTestStep2";
import AbTestStep3 from "./AbTestStep3";
import AbTestStep4 from "./AbTestStep4";
import AbTestStep5 from "./AbTestStep5";
import { toast } from "react-toastify";
import {
  finishAbTest,
  dateFirestoreToReact,
  getAbTestStatus,
  getProjectGoals,
  getAbTestResults,
  getAbTestHeatmapElementsAlternative,
} from "../../helpers";
import { Spinner } from "react-bootstrap";

const AbTestSetup = () => {
  const navigate = useNavigate();
  const selectedProject = useContext(SelectedProjectContext);
  const [currentProject, setCurrentProject] = useState(null);
  const tabItems = [
    { name: 1, label: "1. Select" },
    { name: 2, label: "2. Change content" },
    { name: 3, label: "3. SetUp" },
    { name: 4, label: "4. Test" },
    { name: 5, label: "5. Results" },
  ];
  const [activeTab, setActiveTab] = useState(tabItems[0].name);
  const [pages, setPages] = useState(null);
  const [selectedTest, setSelectedTest] = useState(null);
  const [goals, setGoals] = useState(null);

  const [activeSelectors, setActiveSelectors] = useState([]);

  // Estimation
  const [abSessions, setAbSessions] = useState(0);
  const [bSessions, setBSessions] = useState(0);
  const [weeklySessions, setWeeklySessions] = useState(0);
  const [hasWeekOfData, setHasWeekOfData] = useState(false);
  const [loading, setLoading] = useState(true);

  const [results, setResults] = useState(null);

  const [elementsNew, setElementsNew] = useState([]);

  useEffect(() => {
    // Changing project goes to dashboard
    if (currentProject || !selectedProject?.trackingStartDate) {
      navigate("/ab-test");
    } else {
      setCurrentProject(selectedProject);
    }
  }, [selectedProject]);

  const location = useLocation();

  async function getAndSetAbTestData(idSite, startDate = null, name = null) {
    getAbTestStatus({
      idSite: idSite,
      startDate: startDate,
      name: name,
    }).then((res) => {
      if (res.abSessions === 0 || res.abSessions) setAbSessions(res.abSessions);
      if (res.bSessions === 0 || res.bSessions) setBSessions(res.bSessions);
      if (!selectedProject.trackingStartDate) return;
      if (res.weeklySessions === 0) return;
      const websiteAge =
        new Date() - dateFirestoreToReact(selectedProject.trackingStartDate);
      const websiteAgeInDays = Math.floor(websiteAge / (1000 * 60 * 60 * 24));
      setHasWeekOfData(websiteAgeInDays >= 7 && res.weeklySessions > 0);
      setWeeklySessions(res.weeklySessions);
      setLoading(false);
    });
  }

  async function getAndSetElementsNew(selectedTest) {
    const elements = await getAbTestHeatmapElementsAlternative(
      selectedTest.name,
    );
    if (elements?.[0]?.elements) setElementsNew(elements[0].elements);
  }

  useEffect(() => {
    const state = location.state;
    if (state && state.selectedTest) {
      const tab = state.selectedTest.status === "done" ? 5 : 4;
      setActiveTab(tab);
      setSelectedTest(state.selectedTest);
      getAndSetElementsNew(state.selectedTest);
    } else {
      setActiveSelectors(state?.activeSelectors || []);
      setActiveTab(1);
      setSelectedTest({});
    }
  }, [location]);

  useEffect(() => {
    if (!currentProject || !selectedTest) return;

    if (selectedTest.idSite && selectedTest.startDate && selectedTest.name) {
      getAndSetAbTestData(
        selectedTest.idSite,
        selectedTest.startDate.seconds,
        selectedTest.name,
      );
    } else {
      getAndSetAbTestData(currentProject.matomoId);
    }

    getProjectGoals(currentProject.value).then((res) => {
      setGoals(res ? res : []);
    });
  }, [currentProject, selectedTest]);

  async function finishAbTestHandler(selectedTest) {
    await finishAbTest(selectedTest);
    toast.success("A/B test manually completed!");
    navigate("/ab-test");
  }

  async function getAbTestResultsHandler() {
    getAbTestResults({ abTestId: selectedTest.id }).then((res) => {
      setResults(res);
    });
  }

  useEffect(() => {
    if (selectedTest?.id) getAbTestResultsHandler();
  }, [activeTab, selectedTest]);

  return (
    <div className="abtest-container">
      <div className="abtest-back-to-dashboard-btn">
        <BackToPageCTA url="/ab-test" text="Back to A/B dashboard" />
      </div>

      <div className="fw-700 fs-24 fc-black mb-50">A/B Test</div>
      <div
        className={`mb-30 ${activeTab === 1 ? "mr--100" : ""}`}
        onClick={() =>
          toast.warn(
            "Changing the title or goal is fine, but altering configurations will invalidate the test results.",
          )
        }
      >
        <CustomNavbar
          tabItems={tabItems}
          hasHr={true}
          changeOnClick={false}
          bubbleUpSelection={setActiveTab}
          bubbleDownState={activeTab}
          pastTabClassName="fw-700 fc-black2 fs-16"
          futureTabClassName="fw-700 fc-grey fs-16"
        />
      </div>
      {loading ? (
        <div className="w-100 vhc mt-50">
          <Spinner className="size-100"></Spinner>
        </div>
      ) : activeTab === 1 ? (
        <AbTestStep1
          selectedTest={selectedTest}
          setSelectedTest={setSelectedTest}
          setActiveTab={setActiveTab}
          activeSelectors={activeSelectors}
        />
      ) : activeTab === 2 ? (
        <AbTestStep2
          selectedTest={selectedTest}
          setSelectedTest={setSelectedTest}
          setActiveTab={setActiveTab}
        />
      ) : activeTab === 3 ? (
        <AbTestStep3
          selectedTest={selectedTest}
          setSelectedTest={setSelectedTest}
          setActiveTab={setActiveTab}
          weeklySessions={weeklySessions}
          hasWeekOfData={hasWeekOfData}
          goals={goals}
        />
      ) : activeTab === 4 ? (
        <AbTestStep4
          selectedTest={selectedTest}
          finishAbTest={finishAbTestHandler}
          weeklySessions={weeklySessions}
          hasWeekOfData={hasWeekOfData}
          abSessions={abSessions}
          bSessions={bSessions}
          goals={goals}
        />
      ) : activeTab === 5 ? (
        <AbTestStep5
          selectedTest={selectedTest}
          elementsNew={elementsNew ?? []}
          results={results}
          goals={goals}
        />
      ) : null}
    </div>
  );
};

export default AbTestSetup;
